/**
 * `/finance/ledger` — balanced ledger (§10.3): account balances, Σdebits − Σcredits (must be 0), journals with
 * their lines, per-lottery finance (gross, fees, refunds, chargebacks, taxes, prize cost → net ≠ gross) and CSV export.
 */
import { useQuery } from '@tanstack/react-query';
import { Button, Input, useIntl } from '@windrop/ui';
import { useState } from 'react';
import { SectionTitle } from '../../components/Card';
import { DataTable } from '../../components/DataTable';
import { FilterBar, FilterField } from '../../components/FilterBar';
import { PageHeader } from '../../components/PageHeader';
import { StatCard } from '../../components/StatCard';
import { ToneBadge } from '../../components/StatusBadge';
import { buildUrl } from '../../lib/api/client';
import { financeApi } from '../../lib/api/finance';
import { FinanceNav } from './FinanceNav';

export function LedgerPage() {
  const intl = useIntl();
  const [lotteryId, setLotteryId] = useState('');
  const ledger = useQuery({
    queryKey: ['finance', 'ledger', lotteryId],
    queryFn: () => financeApi.ledger({ ...(lotteryId ? { lotteryId } : {}), limit: 50 }),
  });
  const fin = useQuery({
    queryKey: ['finance', 'lottery', lotteryId],
    queryFn: () => financeApi.lotteryFinance(lotteryId),
    enabled: lotteryId.length === 26,
  });
  const d = ledger.data;
  const f = fin.data;
  const m = (x: { amountMinor: string; currency: string }) => intl.formatMoney(x.amountMinor, x.currency);
  return (
    <>
      <PageHeader
        title="Finance — ledger"
        description="Immutable, balanced debit/credit journals. Status fields aid workflow; the ledger is the financial record. Corrections are reversing entries."
        breadcrumb={[{ label: 'Finance', to: '/finance' }, { label: 'Ledger' }]}
        actions={
          <Button
            href={buildUrl('/v1/admin/ledger', { format: 'csv', ...(lotteryId ? { lotteryId } : {}) })}
            variant="secondary"
          >
            Export CSV
          </Button>
        }
      />
      <FinanceNav />
      <FilterBar>
        <FilterField label="Lottery id" htmlFor="ledger-lottery" grow>
          <Input
            id="ledger-lottery"
            value={lotteryId}
            onChange={(e) => setLotteryId(e.target.value.trim())}
            placeholder="Scopes balances and journals; shows the lottery's finance summary"
            data-testid="ledger-lottery"
          />
        </FilterField>
      </FilterBar>
      <div className="wdc-stats">
        <StatCard
          label="Ledger balance check"
          value={d ? (d.balanced ? 'Balanced' : 'IMBALANCED') : null}
          loading={ledger.isPending}
          signal={
            d
              ? {
                  tone: d.balanced ? 'success' : 'error',
                  text: d.imbalance.map((i) => `${i.currency} ${i.amountMinor}`).join(', ') || 'no journals',
                }
              : undefined
          }
        />
        {f ? (
          <>
            <StatCard
              label="Gross volume"
              value={m(f.grossVolume)}
              hint={`${f.payments.succeeded} succeeded · ${f.payments.pending} pending · ${f.payments.failed} failed`}
            />
            <StatCard
              label="Net revenue"
              value={m(f.netRevenue)}
              hint="gross − fees − refunds − chargebacks − taxes − prize cost"
              signal={
                f.blockingExceptions
                  ? { tone: 'error', text: `${f.blockingExceptions} exception(s) block the draw` }
                  : { tone: 'success', text: `${f.openExceptions} open exception(s)` }
              }
            />
          </>
        ) : null}
      </div>
      {f ? (
        <dl className="wdc-facts">
          <div>
            <dt>Provider fees</dt>
            <dd>{m(f.providerFees)}</dd>
          </div>
          <div>
            <dt>Refunds</dt>
            <dd>{m(f.refunds)}</dd>
          </div>
          <div>
            <dt>Chargebacks</dt>
            <dd>{m(f.chargebacks)}</dd>
          </div>
          <div>
            <dt>Taxes</dt>
            <dd>{m(f.taxes)}</dd>
          </div>
          <div>
            <dt>Prize cost</dt>
            <dd>{m(f.prizeCost)}</dd>
          </div>
          <div>
            <dt>Payouts payable</dt>
            <dd>{m(f.payoutsPayable)}</dd>
          </div>
        </dl>
      ) : null}
      <section aria-labelledby="ledger-balances" className="wd-stack">
        <SectionTitle title={<span id="ledger-balances">Account balances</span>} />
        <DataTable
          caption="Account balances"
          rows={d?.balances ?? []}
          rowKey={(b) => `${b.account}-${b.currency}`}
          loading={ledger.isPending}
          pageSize={0}
          filterable={false}
          columns={[
            { id: 'account', header: 'Account', cell: (b) => b.account },
            { id: 'debits', header: 'Debits', cell: (b) => m(b.debits), align: 'end' },
            { id: 'credits', header: 'Credits', cell: (b) => m(b.credits), align: 'end' },
            {
              id: 'balance',
              header: 'Balance',
              cell: (b) => <span className="wd-tabular">{m(b.balance)}</span>,
              align: 'end',
            },
          ]}
        />
      </section>
      <section aria-labelledby="ledger-journals" className="wd-stack">
        <SectionTitle title={<span id="ledger-journals">Journals</span>} />
        {d?.transactions.items.length === 0 ? <p className="wd-muted">No journals yet.</p> : null}
        <ul className="wd-stack">
          {(d?.transactions.items ?? []).map((t) => (
            <li key={t.journalId} className="wdc-panel">
              <strong>{t.kind}</strong> · {intl.formatDateTime(t.postedAt)} · ref <code>{t.reference}</code>{' '}
              {t.reversesJournalId ? <ToneBadge tone="info">reversal</ToneBadge> : null}
              <table className="wdc-table">
                <caption className="sr-only">Lines of journal {t.journalId}</caption>
                <thead>
                  <tr>
                    <th scope="col">Account</th>
                    <th scope="col">Amount (debit +, credit −)</th>
                    <th scope="col">Memo</th>
                  </tr>
                </thead>
                <tbody>
                  {t.lines.map((l) => (
                    <tr key={l.lineId}>
                      <td>{l.account}</td>
                      <td className="wd-tabular">{m(l.amount)}</td>
                      <td>{l.memo}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </li>
          ))}
        </ul>
      </section>
    </>
  );
}
